import { useState, useCallback, useMemo, useRef } from 'react';
import AuthService from '../services/authService';
import FriendsServiceDB from '../services/friendsServiceDB';
import SavedItemsService from '../services/savedItemsService';
import SavedItemsServiceDB from '../services/savedItemsServiceDB';
import PreferenceLearningService from '../services/preferenceLearningService';
import ActivitySignalsService from '../services/activitySignalsService';
import SocialServiceDB from '../services/socialServiceDB';
import { invalidateUserContentCaches, invalidateLikeStateCaches } from '../utils/cacheInvalidation';
import { dropCardsFromUser } from '../utils/blockedCards';
import useDropBlockedUser from './useDropBlockedUser';
import { withSaveCount, withSaveStanding, POST_SAVE_COUNT_TYPES } from '../utils/postSaveCount';

/**
 * Custom hook managing all modal state and related action handlers for the
 * Discover/Social Feed page.
 *
 * Manages: Comments, Save/Travel Boards, Schedule, Invite, Not Interested,
 * Sharing modals + feedback (thumbs up/down), like/save/share handlers.
 *
 * @param {object} params
 * @param {function} params.setFeedItems - Setter for feed items (unified feed)
 */
function useFeedModals({
  setFeedItems
}) {
  // Comments modal
  const [commentsModalOpen, setCommentsModalOpen] = useState(false);
  const [selectedItemForComments, setSelectedItemForComments] = useState(null);

  // Save modal
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [selectedItemForSave, setSelectedItemForSave] = useState(null);

  // Schedule activity modal
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [activityToSchedule, setActivityToSchedule] = useState(null);

  // Invite friends modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteActivityId, setInviteActivityId] = useState(null);
  const [inviteActivityData, setInviteActivityData] = useState(null);

  // Not interested modal
  const [showNotInterestedModal, setShowNotInterestedModal] = useState(false);
  const [notInterestedActivity, setNotInterestedActivity] = useState(null);

  // Sharing modal
  const [showSharingModal, setShowSharingModal] = useState(false);
  const [sharingItem, setSharingItem] = useState(null);
  const [sharingType, setSharingType] = useState(null);

  // Report modal
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportItem, setReportItem] = useState(null);

  // Feedback tracking (contentId → signal_value: 1 or -1)
  const [feedbackCache, setFeedbackCache] = useState({});

  // A block made anywhere — a card here, a report, the comments modal, a
  // profile — takes the person's cards off this list (C74). FriendsServiceDB
  // announces it only once the server has the block.
  useDropBlockedUser(useCallback((userId) => {
    setFeedItems(prev => dropCardsFromUser(prev, userId));
  }, [setFeedItems]));

  // Current user ID from auth token
  const currentUserId = useMemo(() => {
    try {
      const token = AuthService.getAuthToken();
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.userId;
      }
    } catch (err) {
      console.error('Error parsing auth token:', err);
    }
    return null;
  }, []);

  // Helper to toggle like fields on a matching item
  const toggleLikeFields = (existing) => ({
    ...existing,
    user_liked: !existing.user_liked,
    like_count: existing.user_liked ? (existing.like_count || 1) - 1 : (existing.like_count || 0) + 1
  });

  // One like request per card at a time: the success path settles on the
  // server's absolute state while a failure reverts relatively, and two
  // overlapping taps mixing the two would leave the heart opposite the server.
  const likeInFlightRef = useRef(new Set());

  const handleLike = useCallback(async (item) => {
    // Optimistic update — toggle immediately, revert on failure
    const itemId = item.id;
    const key = String(itemId);
    if (likeInFlightRef.current.has(key)) return;
    likeInFlightRef.current.add(key);
    const revert = () => setFeedItems(prev => prev.map(feedItem =>
      String(feedItem.id) === String(itemId)
        ? toggleLikeFields(feedItem)
        : feedItem
    ));
    setFeedItems(prev => prev.map(feedItem =>
      String(feedItem.id) === String(itemId)
        ? toggleLikeFields(feedItem)
        : feedItem
    ));
    try {
      // toggleLike answers { success: false } rather than throwing — a 404
      // when the viewer can no longer see the item (C59: made private,
      // unfriended, off the trip since the feed loaded) — so a failure is
      // read from the result, not only from a throw.
      const result = await FriendsServiceDB.toggleLike(item.content_type, item.id);
      if (!result?.success) {
        revert();
        return;
      }
      // The toggle is state-keyed: a card whose heart was stale (liked
      // meanwhile on the trip page or in another tab) had its tap go the
      // other way. Settle on the state the server reports (BH-18-05).
      if (typeof result.active === 'boolean') {
        setFeedItems(prev => prev.map(feedItem =>
          String(feedItem.id) === String(itemId) && !!feedItem.user_liked !== result.active
            ? toggleLikeFields(feedItem)
            : feedItem
        ));
      }
      // A place page or a destination page elsewhere may hold this card with
      // its old like state in its 60s cache — see invalidateLikeStateCaches
      // for how a stale heart reverses the next tap.
      invalidateLikeStateCaches();
    } catch (err) {
      console.error('Error toggling like:', err);
      revert();
    } finally {
      likeInFlightRef.current.delete(key);
    }
  }, [setFeedItems]);

  // The card the save sheet was opened from: its own type, and whether it
  // shows a "Saved by N" to settle (the sheet reports events as 'activity').
  const saveTargetRef = useRef(null);

  const handleSave = useCallback((item) => {
    saveTargetRef.current = item;
    setSelectedItemForSave(item);
    setSaveModalOpen(true);
  }, []);

  const handleCloseSaveModal = useCallback(() => {
    setSaveModalOpen(false);
    setSelectedItemForSave(null);
  }, []);

  // A card's "Saved by N" moves by one at once, then settles on the server's
  // answer once the save has landed (utils/postSaveCount). Only the latest
  // answer per card is applied: two quick saves must not end on the first.
  const standingSeqRef = useRef(new Map());
  const settleSaveCount = useCallback(async (type, id) => {
    if (!POST_SAVE_COUNT_TYPES.has(type) || id == null) return;
    const key = `${type}:${id}`;
    const seq = (standingSeqRef.current.get(key) || 0) + 1;
    standingSeqRef.current.set(key, seq);
    const standing = await SavedItemsServiceDB.getSaveStanding(type, id);
    if (!standing || standingSeqRef.current.get(key) !== seq) return;
    setFeedItems(prev => prev.map(feedItem =>
      feedItem.id === id && feedItem.content_type === type && typeof feedItem.save_count === 'number'
        ? withSaveStanding(feedItem, standing)
        : feedItem
    ));
  }, [setFeedItems]);

  const handleSaveUpdate = useCallback((savedItem) => {
    const itemId = savedItem?.id || savedItem?.contentId;
    const boardIds = savedItem?.boardIds || [];
    const collectionIds = savedItem?.collectionIds || [];
    const tripCollectionIds = savedItem?.tripCollectionIds || [];
    // A board saved from its card reports only `saved` (UnifiedSaveModal's
    // board branch) — without it a saved board read as unsaved.
    const isSaved = boardIds.length > 0 || collectionIds.length > 0 || tripCollectionIds.length > 0
      || !!savedItem?.saveToActivities || savedItem?.saved === true;

    // Match by ID only — the modal may normalize content_type to 'activity'
    // for events/artist_events, which wouldn't match the feed item's original type
    setFeedItems(prev => prev.map(feedItem =>
      feedItem.id === itemId
        ? withSaveCount(feedItem, isSaved)
        : feedItem
    ));
    const target = saveTargetRef.current;
    if (target && target.id === itemId && typeof target.save_count === 'number') {
      settleSaveCount(target.content_type, itemId);
    }
  }, [setFeedItems, settleSaveCount]);

  const handleShare = useCallback((item) => {
    setSharingItem(item);
    setSharingType(item.content_type);
    setShowSharingModal(true);
  }, []);

  const handleCloseSharingModal = useCallback(() => {
    setShowSharingModal(false);
    setSharingItem(null);
    setSharingType(null);
  }, []);

  const handleOpenComments = useCallback((item) => {
    setSelectedItemForComments(item);
    setCommentsModalOpen(true);
  }, []);

  const handleCloseComments = useCallback(async () => {
    if (selectedItemForComments) {
      const itemId = selectedItemForComments.id;
      const itemType = selectedItemForComments.content_type;
      try {
        const result = await FriendsServiceDB.getContentComments(itemType, itemId);
        // A failed or refused reload knows nothing about the count: leave it.
        if (!result.failed) {
          const realCount = result.stats?.total_comments ?? result.comments?.length ?? 0;
          setFeedItems(prev => prev.map(feedItem => {
            if (feedItem.id === itemId && feedItem.content_type === itemType) {
              return { ...feedItem, comment_count: realCount };
            }
            return feedItem;
          }));
        }
      } catch (err) {
        // If fetch fails, leave count unchanged
      }
    }
    setCommentsModalOpen(false);
    setSelectedItemForComments(null);
  }, [selectedItemForComments, setFeedItems]);

  const handleScheduleActivity = useCallback((activity) => {
    setActivityToSchedule(activity);
    setShowScheduleModal(true);
  }, []);

  const handleScheduleComplete = useCallback((result) => {
    setShowScheduleModal(false);
    if (result?.shouldInviteFriends && result?.activityId) {
      setInviteActivityId(result.activityId);
      setInviteActivityData(result.activity || null);
      setShowInviteModal(true);
    }
    setActivityToSchedule(null);
  }, []);

  const handleSaveActivity = useCallback((activity) => {
    // The card as it is (an activity post keeps its type; a place card's
    // isn't a post, so nothing settles for it).
    saveTargetRef.current = activity;
    setSelectedItemForSave({
      ...activity,
      content_type: 'activity'
    });
    setSaveModalOpen(true);
  }, []);

  const handleUnsaveActivity = useCallback(async (activityId) => {
    const result = await SavedItemsService.unsaveActivity(activityId);
    if (result.success) {
      invalidateUserContentCaches();
      setFeedItems(prev => prev.map(feedItem =>
        (feedItem.id === activityId || feedItem.content_id === activityId) && feedItem.content_type === 'activity'
          ? withSaveCount(feedItem, false)
          : feedItem
      ));
      settleSaveCount('activity', activityId);
    }
  }, [setFeedItems, settleSaveCount]);

  const handleNotInterested = useCallback((activity) => {
    setNotInterestedActivity(activity);
    setShowNotInterestedModal(true);
  }, []);

  const handlePositiveFeedback = useCallback(async (activity) => {
    const contentId = activity.id || activity.place_id;
    const currentFeedback = feedbackCache[contentId];

    if (currentFeedback === 1) {
      try {
        await ActivitySignalsService.undoFeedback(activity);
        setFeedbackCache(prev => {
          const updated = { ...prev };
          delete updated[contentId];
          return updated;
        });
      } catch (err) {
        console.debug('Error undoing feedback:', err);
      }
      return;
    }

    try {
      await ActivitySignalsService.recordPositiveFeedback(activity, {
        source: 'discover_feed'
      });
      setFeedbackCache(prev => ({
        ...prev,
        [contentId]: 1
      }));
    } catch (err) {
      console.debug('Error recording positive feedback:', err);
    }
  }, [feedbackCache]);

  const handleNotInterestedSubmit = useCallback(async (feedback) => {
    const result = PreferenceLearningService.recordNotInterestedFeedback(feedback);

    if (notInterestedActivity) {
      const contentId = notInterestedActivity.id || notInterestedActivity.place_id;
      try {
        await ActivitySignalsService.recordNegativeFeedback(notInterestedActivity, {
          reason: feedback.selectedReason,
          context: feedback.additionalFeedback || null,
          source: 'discover_feed'
        });
        setFeedbackCache(prev => ({
          ...prev,
          [contentId]: -1
        }));
      } catch (err) {
        console.debug('Error recording negative feedback signal:', err);
      }
    }

    if (result.success) {
      setFeedItems(prev => prev.filter(fi =>
        (fi.id || fi.content_id) !== feedback.activityId
      ));
    }
  }, [notInterestedActivity, setFeedItems]);

  const handleReport = useCallback((item) => {
    setReportItem(item);
    setShowReportModal(true);
  }, []);

  const handleReportSubmit = useCallback(async (item, reason, details, shouldBlock) => {
    const contentType = item.content_type || 'activity';
    const contentId = item.id || item.content_id;
    const reportedUserId = item.user_id || item.creator_id || null;

    // reportContent answers { success: false } rather than throwing — a 404
    // when the reporter can no longer see what they are reporting (C59). The
    // feed has no toast of its own, so the failure goes back to ReportModal
    // marked `inline`, which says so instead of confirming.
    let report = null;
    try {
      report = await SocialServiceDB.reportContent(contentType, contentId, reportedUserId, reason, details);
    } catch (err) {
      console.debug('Error recording report:', err);
    }

    // If user opted to block, do that too. A block that lands takes the rest
    // of their cards with it (useDropBlockedUser above).
    if (shouldBlock && reportedUserId) {
      try {
        const { default: FriendsServiceDB } = await import('../services/friendsServiceDB');
        await FriendsServiceDB.blockUser(reportedUserId);
      } catch (err) {
        console.debug('Error blocking user from report:', err);
      }
    }

    // Also record a negative signal to improve feed quality
    try {
      await ActivitySignalsService.recordNegativeFeedback(item, {
        reason: `report_${reason}`,
        context: details || null,
        source: 'discover_feed'
      });
      setFeedbackCache(prev => ({
        ...prev,
        [contentId]: -1
      }));
    } catch (err) {
      console.debug('Error recording negative feedback for report:', err);
    }

    // Remove the reported item from feed
    const itemId = item.id || item.content_id;
    setFeedItems(prev => prev.filter(fi => (fi.id || fi.content_id) !== itemId));

    // A refusal (404: the reporter can no longer see it) reads differently
    // from a failure to send (network, 429, 500), which is worth a retry.
    if (report?.success === false) {
      return { success: false, inline: true, unavailable: report.error === 'Content not found' };
    }
    return report;
  }, [setFeedItems]);

  // Blocking is two-step: handleBlockUser stages the target, the consumer page
  // renders a ConfirmDialog wired to confirmBlockUser/cancelBlockUser.
  const [blockUserTarget, setBlockUserTarget] = useState(null);

  const handleBlockUser = useCallback((item) => {
    const userId = item.user_id || item.creator_id;
    if (!userId) return;
    setBlockUserTarget(item);
  }, []);

  const cancelBlockUser = useCallback(() => setBlockUserTarget(null), []);

  const confirmBlockUser = useCallback(async () => {
    const item = blockUserTarget;
    if (!item) return;
    setBlockUserTarget(null);
    const userId = item.user_id || item.creator_id;

    // A block that lands removes their cards here and on every other mounted
    // list, albums on their trips included, and clears the caches that would
    // bring them back (useDropBlockedUser, C74). One that fails leaves the
    // cards where they are: removing them would say the block had worked.
    try {
      const { default: FriendsServiceDB } = await import('../services/friendsServiceDB');
      const result = await FriendsServiceDB.blockUser(userId);
      if (!result?.success) console.debug('Block refused:', result?.error);
    } catch (err) {
      console.debug('Error blocking user:', err);
    }
  }, [blockUserTarget]);

  // Memoized so `modals` keeps a stable identity across renders where nothing
  // in it changed. useDiscoverCardActions' buildActions is useCallback'd on
  // [modals], and FeedCard is memo()'d on buildActions — so without this, every
  // parent render (e.g. a single like toggling feedItems) minted a fresh modals
  // object, a fresh buildActions, and re-rendered every mounted card. All
  // handlers are useCallback-stable; identity now only changes when modal
  // state actually changes (open/close, staged item, feedback), which is rare.
  return useMemo(() => ({
    // Current user
    currentUserId,

    // Comments
    commentsModalOpen,
    selectedItemForComments,
    handleOpenComments,
    handleCloseComments,

    // Save
    saveModalOpen,
    selectedItemForSave,
    handleSave,
    handleCloseSaveModal,
    handleSaveUpdate,
    handleSaveActivity,
    handleUnsaveActivity,

    // Schedule
    showScheduleModal,
    setShowScheduleModal,
    activityToSchedule,
    setActivityToSchedule,
    handleScheduleActivity,
    handleScheduleComplete,

    // Invite
    showInviteModal,
    setShowInviteModal,
    inviteActivityId,
    setInviteActivityId,
    inviteActivityData,
    setInviteActivityData,

    // Not interested
    showNotInterestedModal,
    setShowNotInterestedModal,
    notInterestedActivity,
    setNotInterestedActivity,
    handleNotInterested,
    handleNotInterestedSubmit,

    // Sharing
    showSharingModal,
    sharingItem,
    sharingType,
    handleShare,
    handleCloseSharingModal,

    // Report
    showReportModal,
    setShowReportModal,
    reportItem,
    setReportItem,
    handleReport,
    handleReportSubmit,

    // Block
    handleBlockUser,
    blockUserTarget,
    confirmBlockUser,
    cancelBlockUser,

    // Feedback
    feedbackCache,
    handlePositiveFeedback,

    // Like
    handleLike
  }), [
    currentUserId,
    commentsModalOpen, selectedItemForComments, handleOpenComments, handleCloseComments,
    saveModalOpen, selectedItemForSave, handleSave, handleCloseSaveModal,
    handleSaveUpdate, handleSaveActivity, handleUnsaveActivity,
    showScheduleModal, activityToSchedule, handleScheduleActivity, handleScheduleComplete,
    showInviteModal, inviteActivityId, inviteActivityData,
    showNotInterestedModal, notInterestedActivity, handleNotInterested, handleNotInterestedSubmit,
    showSharingModal, sharingItem, sharingType, handleShare, handleCloseSharingModal,
    showReportModal, reportItem, handleReport, handleReportSubmit,
    handleBlockUser, blockUserTarget, confirmBlockUser, cancelBlockUser,
    feedbackCache, handlePositiveFeedback,
    handleLike
  ]);
}

export default useFeedModals;
